import React, { useState } from "react";
import { useDispatch } from "react-redux";
import { Link as RouterLink, useNavigate } from "react-router-dom";
import PropTypes from "prop-types";
import moment from "moment";
import excelJs from "exceljs";
import UserListToolbar from "./UserListToolbar";
import UserListHead from "./UserListHead";
import SearchNotFound from "../../../SearchNotFound";
import Label from "../../../Label";
import Page from "../../../Page";

import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import Swal from "sweetalert2";
import { updateResubmission } from "../../../../core/service/resubmission.service";

import {
  Card,
  Table,
  Stack,
  TableRow,
  TableBody,
  TableCell,
  Typography,
  TableContainer,
  TablePagination,
  IconButton,
  Dialog,
  DialogContent,
  DialogActions,
  Button,
  Container,
  Link,
} from "@mui/material";
import { config } from "../../../../core/service/config.service";

const TABLE_HEAD = [
  { id: "name", label: "Name", alignLeft: false },
  { id: "no", label: "No.", alignRight: false },
  { id: "date", label: "Date", alignRight: false },
  { id: "role_nm", label: "Program", alignRight: false },
  { id: "action", label: "Action", alignRight: false },
];

const ROLE_ADMIN = "admin";
const ROLE_MENTOR = "mentor";
const ROLE_PHD3Y = "phd_3y";
const ROLE_PHD5Y = "phd_5y";
const ROLE_POSTDOC = "post_doc";

const descendingComparator = (a, b, orderBy) => {
  if (b[orderBy] < a[orderBy]) {
    return -1;
  }
  if (b[orderBy] > a[orderBy]) {
    return 1;
  }
  return 0;
};

const getComparator = (order, orderBy) => {
  return order === "desc"
    ? (a, b) => descendingComparator(a, b, orderBy)
    : (a, b) => -descendingComparator(a, b, orderBy);
};

const applySortFilter = (array, comparator, query) => {
  const stabilizedThis = array.map((el, index) => [el, index]);
  stabilizedThis.sort((a, b) => {
    const order = comparator(a[0], b[0]);
    if (order !== 0) return order;
    return a[1] - b[1];
  });
  if (query) {
    return array.filter((e) => {
      const { title_en, name_en, middle_en, surname_en } = e;
      const name = `${title_en} ${name_en} ${middle_en || ""} ${surname_en}`;
      return name.toLowerCase().indexOf(query.toLowerCase()) !== -1;
    });
  }
  return stabilizedThis.map((el) => el[0]);
};

ResubmitAdminTable.propTypes = {
  listResubmit: PropTypes.array,
};

export default function ResubmitAdminTable({ listResubmit }) {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [page, setPage] = useState(0);
  const [order, setOrder] = useState("asc");
  const [orderBy, setOrderBy] = useState("name");
  const [filterName, setFilterName] = useState("");
  const [rowsPerPage, setRowsPerPage] = useState(5);

  const [openApprove, setOpenApprove] = useState(false);
  const [openDisapprove, setOpenDisapprove] = useState(false);
  const [nameInModal, setNameInModal] = useState("");

  const closeName = () => {
    setOpenApprove(false);
    setOpenDisapprove(false);
  };

  const handleRequestSort = (event, property) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleFilterByName = (event) => {
    setFilterName(event.target.value);
  };

  const emptyRows =
    page > 0 ? Math.max(0, (1 + page) * rowsPerPage - listResubmit.length) : 0;

  const filteredUsers = applySortFilter(
    listResubmit,
    getComparator(order, orderBy),
    filterName
  );

  const isUserNotFound = filteredUsers.length === 0;

  const approveResubmission = (rsm_id) => {
    Swal.fire({
      title: "Are you resubmission?",
      text: `Do you confirm to approve ${nameInModal} resubmission?`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, approve it!",
    }).then((result) => {
      if (result.isConfirmed) {
        console.log(rsm_id);
        updateResubmission(rsm_id, 1).then((req, res) => {
          Swal.fire({
            icon: "success",
            title: "Successfully",
            showConfirmButton: false,
            timer: 1500,
          });
        });
      }
    });
  };

  const disapproveResubmission = (rsm_id) => {
    Swal.fire({
      title: "Are you resubmission?",
      text: `Do you confirm to disapprove ${nameInModal} resubmission?`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, disapprove it!",
    }).then((result) => {
      if (result.isConfirmed) {
        updateResubmission(rsm_id, 2).then((req, res) => {
          Swal.fire({
            icon: "success",
            title: "Successfully",
            showConfirmButton: false,
            timer: 1500,
          });
        });
      }
    });
  };

  const exportExcel = async () => {
    const workbook = new excelJs.Workbook();
    const ws = workbook.addWorksheet(`Report`);
    ws.columns = [
      {
        header: "Name",
        key: "name_en",
        width: 10,
        style: {
          alignment: { horizontal: "center", vertical: "middle" },
          font: { bold: true },
        },
      },
      {
        header: "No.",
        key: "no",
        width: 10,
        style: {
          alignment: { horizontal: "center", vertical: "middle" },
          font: { bold: true },
        },
      },
      {
        header: "Date",
        key: "created_date",
        width: 10,
        style: {
          alignment: { horizontal: "center", vertical: "middle" },
          font: { bold: true },
        },
      },
      {
        header: "Program",
        key: "role_nm",
        width: 10,
        style: {
          alignment: { horizontal: "center", vertical: "middle" },
          font: { bold: true },
        },
      },
      {
        header: "Action",
        key: "action",
        width: 10,
        style: {
          alignment: { horizontal: "center", vertical: "middle" },
          font: { bold: true },
        },
      },
    ];

    ws.getRow(1).height = 30;

    await Promise.all(
      filteredUsers.map(async (val, index) => {
        ws.addRow([
          `${val.name_en} ${val.middle_en || ""} ${val.surname_en}`,
          val.id,
          val.created_date,
          val.role_nm === ROLE_POSTDOC
            ? "Post Doc"
            : val.role_nm === ROLE_PHD3Y
            ? "PhD 3 year"
            : "PhD 5 year",
          val.admin_approve === 1 ? "Approve" : "Disapprove",
        ]);
        ws.getRow(index + 2).font = {
          ...ws.getRow(index + 2).font,
          bold: false,
        };
      })
    );

    workbook.xlsx
      .writeBuffer({
        base64: true,
      })
      .then((xls64) => {
        // build anchor tag and attach file (works in chrome)
        var a = document.createElement("a");
        var data = new Blob([xls64], {
          type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        });

        var url = URL.createObjectURL(data);
        a.href = url;
        a.download = `RESUBMISSION_${moment().format("YYYYMMDD_HHmmss")}.xlsx`;
        document.body.appendChild(a);
        a.click();
        setTimeout(function () {
          document.body.removeChild(a);
          window.URL.revokeObjectURL(url);
        }, 0);
      })
      .catch(function (error) {
        console.log(error.message);
      });
  };

  return (
    <Page title="Resubmit Admin">
      <Container>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          mb={5}
        >
          <Typography variant="h4" gutterBottom>
            Resubmission
          </Typography>

          <Link href={`${config.host_api}export/resubmission`}>
            <Button variant="contained">Export excel</Button>
          </Link>
        </Stack>

        <Card>
          <UserListToolbar
            filterName={filterName}
            onFilterName={handleFilterByName}
          />

          <TableContainer sx={{ minWidth: 800 }}>
            <Table>
              <UserListHead
                order={order}
                orderBy={orderBy}
                headLabel={TABLE_HEAD}
                rowCount={listResubmit.length}
                onRequestSort={handleRequestSort}
              />
              <TableBody>
                {filteredUsers
                  .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                  .map((row) => {
                    const {
                      admin_approve,
                      id,
                      name_en,
                      middle_en,
                      role_nm,
                      surname_en,
                      created_date,
                    } = row;

                    return (
                      <TableRow tabIndex={-1} hover key={id}>
                        <TableCell />

                        <TableCell
                          component="th"
                          scope="row"
                          padding="none"
                          align="right"
                        >
                          <Stack
                            direction="row"
                            alignItems="center"
                            spacing={2}
                          >
                            <Typography>
                              {`${name_en} ${middle_en} ${surname_en}` || ""}
                            </Typography>
                          </Stack>
                        </TableCell>

                        <TableCell align="left">{id}</TableCell>

                        <TableCell
                          sx={{ textTransform: "capitalize" }}
                          align="left"
                        >
                          {created_date}
                        </TableCell>
                        <TableCell align="left">
                          {role_nm === ROLE_POSTDOC
                            ? "Post Doc"
                            : role_nm === ROLE_PHD3Y
                            ? "PhD 3 year"
                            : "PhD 5 year"}
                        </TableCell>

                        <TableCell align="left">
                          {admin_approve === 0 ? (
                            <Stack direction="row">
                              <IconButton
                                color="success"
                                onClick={() => {
                                  approveResubmission(id);
                                }}
                              >
                                <CheckCircleIcon />
                              </IconButton>
                              <IconButton
                                color="error"
                                onClick={() => {
                                  disapproveResubmission(id);
                                }}
                              >
                                <CancelIcon />
                              </IconButton>
                            </Stack>
                          ) : (
                            <Label
                              variant="ghost"
                              color={admin_approve === 1 ? "success" : "error"}
                            >
                              {admin_approve === 1 ? "Approve" : "Disapprove"}
                            </Label>
                          )}
                        </TableCell>

                        <Dialog
                          open={openApprove}
                          onClose={closeName}
                          fullWidth={true}
                        >
                          <DialogContent>
                            Do you confirm to approve {nameInModal}{" "}
                            resubmission?
                          </DialogContent>
                          <DialogActions>
                            <Button onClick={closeName} autoFocus>
                              Confirm
                            </Button>
                          </DialogActions>
                        </Dialog>

                        <Dialog
                          open={openDisapprove}
                          onClose={closeName}
                          fullWidth={true}
                        >
                          <DialogContent>
                            Do you confirm to disapprove {nameInModal}{" "}
                            resubmission?
                          </DialogContent>
                          <DialogActions>
                            <Button onClick={closeName} autoFocus>
                              Confirm
                            </Button>
                          </DialogActions>
                        </Dialog>
                      </TableRow>
                    );
                  })}
                {emptyRows > 0 && (
                  <TableRow style={{ height: 53 * emptyRows }}>
                    <TableCell colSpan={6} />
                  </TableRow>
                )}
              </TableBody>
              {isUserNotFound && (
                <TableBody>
                  <TableRow>
                    <TableCell align="center" colSpan={6} sx={{ py: 3 }}>
                      <SearchNotFound searchQuery={filterName} />
                    </TableCell>
                  </TableRow>
                </TableBody>
              )}
            </Table>
          </TableContainer>

          <TablePagination
            rowsPerPageOptions={[5, 10, 25]}
            component="div"
            count={listResubmit.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </Card>
      </Container>
    </Page>
  );
}
